import { useEffect, useRef, useState } from "react";

import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { TitleAndSpecialtyModel } from "@/Common/Models/MedfarDictionaries/TitleAndSpecialtyModel";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { FaxDirectoryExternalConsultantSelection } from "./FaxDirectoryExternalConsultantSelection";
import { IConsultantProps } from "./IConsultantProps";

interface IConsultantSelectionProps extends IConsultantProps {
    patientClinicianModel: IPatientCliniciansModel;
    redx: React.CSSProperties;
    onSpecialtySelect: (specialty: TitleAndSpecialtyModel) => void;
    onClearSpecialtySelection: () => void;
    onSelectDirectoryFaxNumber: (faxNumberId: string) => void;
    onClearIntervenorSelection: () => void
    onNoteChange: (noteValue: string) => void;
}

export function ConsultantSelection(props: IConsultantSelectionProps): JSX.Element {
    const { getText } = useTranslation();
    const [specialtyForSearch, setSpecialtyForSearch] = useState<string>(props.intervenor.GetSpecialityForSearch());
    const [filteredSpecialties, setFilteredSpecialties] = useState<Immutable.List<SearchableItemModelT<TitleAndSpecialtyModel>>>(Immutable.List([]));
    const isInitialMount = useRef(true);

    /**
     * Get the specialities of an intervenor that are not yet related to the current patient file
     * @returns {Immutable.List<SearchableItemModelT<SpecialtyModel>>}
     */
    const getFilteredSpecialty = (): Immutable.List<SearchableItemModelT<TitleAndSpecialtyModel>> => {
        if (!props.intervenor || !props.patientClinicianModel)
            return cacheStore.TitlesAndSpecialtiesCache;

        const specialties = props.intervenor.initSearchableEmployeeSpecialties();
        const updatedSpecialties = (isInitialMount.current && specialties.any())
            ? specialties.toImmutable()
            : cacheStore.TitlesAndSpecialtiesCache;

        if (isInitialMount.current) {
            // Skip the initial update to avoid unnecessary re-rendering
            isInitialMount.current = false;
        }

        return updatedSpecialties;
    };

    useEffect(() => setFilteredSpecialties(getFilteredSpecialty()), []);

    useEffect(() => {
        if (isInitialMount.current) {
            // Skip the initial update to avoid unnecessary re-rendering
            isInitialMount.current = false;
        }
        else {
            setFilteredSpecialties(getFilteredSpecialty());
        }
    }, [specialtyForSearch]);

    const isSpecialtyValid = TitleAndSpecialtyModel.isValid(props.intervenor.getTitleAndSpecialtyModel());

    return (
        <div>
            {!props.intervenor.Employee && props.intervenor.Model.HealthCareProvider
                ? (
                    <FaxDirectoryExternalConsultantSelection
                        healthCareProvider={DirectoryHealthCareProvider.fromSearchDto(props.intervenor.Model.HealthCareProvider)}
                        note={props.intervenor.Model.Intervenor.Note}
                        onClearIntervenorSelection={() => props.onClearIntervenorSelection()}
                        onHealthCareProviderSelect={provider => props.onHealthCareProviderSelect(provider, true)}
                        onSelectDirectoryFaxNumber={id => props.onSelectDirectoryFaxNumber(id)}
                        selectedFaxNumberId={props.intervenor.Model.Intervenor?.DirectoryFaxNumberId}
                        onNoteChange={props.onNoteChange}
                    />
                )
                : (
                    <div>
                        <div>
                            <span>{`${getText("Staff1Label")} : `}</span>
                        </div>
                        <div className="flex-container margin-xs-top margin-sm-left flex-start">
                            <div>
                                <span>{`${props.intervenor.GetName()}  ${props.intervenor.GetLicence(true)}`}</span>
                            </div>
                            <div className="margin-lg-left">
                                <a onClick={() => props.onClearIntervenorSelection()} style={props.redx}> X</a>
                            </div>
                        </div>

                        <div className="margin-md-top">
                            <div>
                                <label htmlFor="txtSpeciality_ClinicianEdit_Add">{`${getText("SpecializationLabel")} : `}</label>
                            </div>
                            {isSpecialtyValid && (
                                <div className="flex-container margin-xs-top margin-sm-left flex-start">
                                    <div>
                                        <span>{props.intervenor.getSpecialtyName()}</span>
                                    </div>
                                    <div className="margin-lg-left">
                                        <a onClick={() => props.onClearSpecialtySelection()} style={props.redx}> X</a>
                                    </div>
                                </div>
                            )}
                            {!isSpecialtyValid && (
                                <div className="margin-sm-left">
                                    <TextboxControl
                                        debounceMs={200}
                                        limit={100}
                                        value={specialtyForSearch}
                                        className="form-control"
                                        RemoveStyle={true}
                                        autoCompleteItems={filteredSpecialties}
                                        onClickRestrictedSearch={true}
                                        onAutoCompleteSelectCallBack={(_, model: TitleAndSpecialtyModel) => {
                                            props.onSpecialtySelect(model);
                                        }}
                                        placeholder=""
                                        id="txtSpeciality_ClinicianEdit_Add"
                                        dataCy="consultant-speciality"
                                        valid={props.intervenor.Model.LicenceOrganisationRule != null}
                                        onChange={value => setSpecialtyForSearch(value)}
                                    />
                                </div>
                            )}
                        </div>

                        {TitleAndSpecialtyModel.isValid(props.intervenor.getTitleAndSpecialtyModel()) && (
                            <div className="margin-md-top">
                                <div>
                                    <span>{`${getText("OrganizationLabel")} : `}</span>
                                </div>
                                <div className="flex-container margin-xs-top margin-sm-left flex-start">
                                    <span>{props.intervenor.GetOrganisationName()}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )
            }
        </div>
    );
}
